import React, { useState, useEffect } from 'react';
import { formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import ExcelPreviewDialog from '../TreasuryPayment/ExcelPreviewDialog';
import PDFPreviewDialog from '../TreasuryPayment/PDFPreviewDialog';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDataGridSx,
  harmonateCardSx,
  harmonateDialogCloseIconSx,
} from '../../../theme/harmonateStyles';

// Utility: Get tenant_id from localStorage
const getTenantId = () => localStorage.getItem('tenant_id');

const BankReference = ({ onBack }) => {
  const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');
  const [referenceFileData, setReferenceFileData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Preview state (same as Treasury Reference)
  const [excelPreviewOpen, setExcelPreviewOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const { showSuccess, showError } = useToast();
  const { canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);

  // Load reference file data and initialize formats on mount
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) {
          setDateTimeFormat(config.DateTimeFormat);
        }
      });
    }
    fetchReferenceFileData();
  }, []);

  // Fetch reference file data and file instances (Bank category)
  const fetchReferenceFileData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const tenantId = getTenantId();

      if (!token || !tenantId) {
        throw new Error('Missing token or tenant ID');
      }

      // Fetch document types for GL category (same as GL Reference), document type is "Cash Recon Bank"
      const docTypesResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/GL`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!docTypesResponse.ok) {
        throw new Error(`Failed to fetch document types: ${docTypesResponse.status}`);
      }

      const docTypes = await docTypesResponse.json();

      if (!docTypes || docTypes.length === 0) {
        throw new Error('No document types found for GL category');
      }

      // Find the Cash Recon Bank document type
      const bankRefDocType = docTypes.find(dt => dt.name === 'Cash Recon Bank');

      if (!bankRefDocType || !bankRefDocType.id) {
        throw new Error('Cash Recon Bank document type not found');
      }

      // Fetch file instances for Cash Recon Bank document type
      const fileInstancesResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-document-type/${bankRefDocType.id}?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!fileInstancesResponse.ok) {
        throw new Error(`Failed to fetch file instances: ${fileInstancesResponse.status}`);
      }

      let fileInstances = await fileInstancesResponse.json();

      // Sort file instances by createdAt in descending order (newest first)
      fileInstances = fileInstances.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      const combinedData = {
        ...bankRefDocType,
        fileInstances: fileInstances || []
      };

      setReferenceFileData([combinedData]);
      showSuccess('Bank Reference Files data loaded successfully');
    } catch (err) {
      console.error('Error fetching bank reference file data:', err);
      setError(err.message || 'Failed to load data');
      showError('Failed to load bank reference files data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchReferenceFileData();
  };

  // Preview handlers (same structure as GL Reference)
  const handlePreviewClick = (event, row) => {
    event.stopPropagation();
    setPreviewFile(row);

    const contentType = row.contentType || row.content_type || '';

    if (contentType.includes('pdf')) {
      setPdfPreviewOpen(true);
    } else if (contentType.includes('excel') || contentType.includes('spreadsheet') ||
      contentType.includes('xlsx') || contentType.includes('xls')) {
      setExcelPreviewOpen(true);
    } else {
      const fileName = row.name || '';
      const extension = fileName.split('.').pop()?.toLowerCase();

      if (extension === 'pdf') {
        setPdfPreviewOpen(true);
      } else if (['xlsx', 'xls'].includes(extension)) {
        setExcelPreviewOpen(true);
      } else {
        showError('Preview not available for this file type');
      }
    }
  };

  const handleCloseExcelPreview = () => {
    setExcelPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleRowClick = (params) => {
    console.log('Row clicked:', params.row);
  };

  // Delete handlers
  const handleDeleteClick = (event, row) => {
    if (!canDelete) return;
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete) return;
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileToDelete.id}/delete`;
      const response = await fetch(deleteUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete file: ${response.status} - ${errorText}`);
      }

      showSuccess('File deleted successfully');
      handleDeleteClose();
      fetchReferenceFileData();
    } catch (error) {
      console.error('Error during file deletion:', error);
      showError(`Failed to delete file: ${error.message}`);
    }
  };

  // Handle info click - fetch audit logs
  const handleInfoClick = async (event, row) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${row.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }

      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);
      setActiveTab(0);
      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Early loading state
  if (loading && referenceFileData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: ht.green }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: ht.listBg, minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.9rem' }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={onBack}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon sx={{ fontSize: '1rem' }} />
            Cash Reconciliation
          </Link>
          <Typography color="text.primary">
            Bank Reference Files
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Paper elevation={0} sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', mb: 3, height: 'calc(100vh - 200px)' }}>
        <DataGridPro
          rows={referenceFileData.length > 0 && referenceFileData[0].fileInstances ? referenceFileData[0].fileInstances : []}
          rowHeight={40}
          columns={[
            {
              field: 'name',
              headerName: 'File Name',
              flex: 2,
              resizable: true,
              renderCell: (params) => (
                <Box sx={{ fontWeight: 'normal' }}>
                  {params.value}
                </Box>
              )
            },
            {
              field: 'property',
              headerName: 'Property',
              flex: 1,
              resizable: true,
              valueGetter: (params) => {
                try {
                  if (params.row.additionalDetails) {
                    const details = typeof params.row.additionalDetails === 'string'
                      ? JSON.parse(params.row.additionalDetails) : params.row.additionalDetails;
                    return details.property || 'N/A';
                  }
                  return 'N/A';
                } catch (error) {
                  return 'N/A';
                }
              },
              renderCell: (params) => {
                let property = 'N/A';
                try {
                  if (params.row.additionalDetails) {
                    const details = typeof params.row.additionalDetails === 'string'
                      ? JSON.parse(params.row.additionalDetails) : params.row.additionalDetails;
                    property = details.property || 'N/A';
                  }
                } catch (error) {
                  property = 'N/A';
                }
                return (
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    {property}
                  </Typography>
                );
              }
            },
            {
              field: 'username',
              headerName: 'Uploaded By',
              flex: 1,
              resizable: true,
              renderCell: (params) => (
                <Box sx={{ fontWeight: 'normal' }}>
                  {params.value || 'N/A'}
                </Box>
              )
            },
            {
              field: 'createdAt',
              headerName: 'Date',
              flex: 1,
              resizable: true,
              renderCell: (params) => (
                <Box sx={{ fontWeight: 'normal' }}>
                  {params.value ? formatDateTime(params.value, dateTimeFormat) : 'N/A'}
                </Box>
              )
            },
            {
              field: 'processStep',
              headerName: 'Process Step',
              flex: 1,
              resizable: true,
              renderCell: (params) => (
                <Box sx={{ fontWeight: 'normal' }}>
                  {params.value || 'N/A'}
                </Box>
              )
            },
            {
              field: 'preview',
              headerName: '',
              width: 60,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => '',
              renderCell: (params) => (
                <Tooltip title="Preview File">
                  <IconButton
                    size="small"
                    onClick={(e) => handlePreviewClick(e, params.row)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            },
            {
              field: 'info',
              headerName: '',
              width: 60,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => '',
              renderCell: (params) => (
                <Tooltip title="View Document Details & Audit Logs">
                  <IconButton
                    size="small"
                    onClick={(e) => handleInfoClick(e, params.row)}
                    sx={{ color: ht.green }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            },
            {
              field: 'delete',
              headerName: '',
              width: 60,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => '',
              renderCell: (params) => (
                <Tooltip title={canDelete ? 'Delete file' : 'No delete permission'}>
                  <span>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, params.row)}
                    disabled={!canDelete}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  </span>
                </Tooltip>
              ),
            },
          ]}
          onRowClick={handleRowClick}
          sx={{ ...harmonateDataGridSx, border: 'none', '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' } }}
          disableSelectionOnClick
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          noRowsOverlay={() => (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {loading ? 'Loading file instances...' : 'No file instances found'}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                sx={{ color: '#1976D2', borderColor: '#1976D2' }}
              >
                Refresh
              </Button>
            </Box>
          )}
        />
      </Paper>

      {/* Document Details & Audit Logs Modal - same design as GL Reference */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => {
            setIsLogsModalOpen(false);
            setActiveTab(0);
          }}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              ...harmonateCardSx,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${ht.line}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: ht.green,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: ht.white, fontSize: '1.1rem' }}
              >
                Document Details & Audit Logs
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setIsLogsModalOpen(false);
                  setActiveTab(0);
                }}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.green }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {documentDetails && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      '& > div': { minWidth: 0 }
                    }}>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Name</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Type</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.documentTypeName || documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Uploaded At</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {(documentDetails.uploadAt || documentDetails.createdAt) ? formatDateTime(documentDetails.uploadAt || documentDetails.createdAt, dateTimeFormat) : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Location</Typography>
                        <Tooltip title={documentDetails.location || 'N/A'} arrow placement="top">
                          <Typography variant="body2" noWrap sx={{
                            maxWidth: '300px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: 'inline-block',
                            color: ht.text,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              textDecorationColor: ht.text,
                              textDecorationThickness: '1px',
                              textUnderlineOffset: '2px',
                            }
                          }}>
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Unit of Work</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.unitOfWorkId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Integration ID</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: `1px solid ${ht.line}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                      AUDIT LOGS
                    </Typography>
                  </Box>
                  {auditLogs.length > 0 ? (
                    <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                      <DataGridPro
                        rows={auditLogs}
                        columnResizeMode="onEnd"
                        resizable
                        pagination
                        rowsPerPageOptions={[10, 25, 50]}
                        pageSize={10}
                        autoPageSize={false}
                        columns={[
                          {
                            field: 'createdAt',
                            headerName: 'Timestamp',
                            flex: 1,
                            minWidth: 160,
                            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Timestamp
                              </Typography>
                            )
                          },
                          {
                            field: 'service',
                            headerName: 'Service',
                            flex: 0.8,
                            minWidth: 150,
                            cellClassName: 'service-cell',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Service
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                bgcolor: params.value === 'upload' ? ht.secondaryTab :
                                  params.value === 'document_classification' ? 'rgba(2, 107, 29, 0.12)' :
                                    params.value === 'extraction' ? 'rgba(46, 166, 160, 0.18)' :
                                      params.value === 'ingest' ? 'rgba(1, 48, 98, 0.12)' : ht.listBg,
                                color: params.value === 'upload' ? ht.text :
                                  params.value === 'document_classification' ? ht.green :
                                    params.value === 'extraction' ? ht.blue :
                                      params.value === 'ingest' ? ht.text : ht.text,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: ht.buttonRadius,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'fit-content',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'action',
                            headerName: 'Action',
                            flex: 1,
                            minWidth: 120,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Action
                              </Typography>
                            ),
                            valueFormatter: (params) => params.value || 'N/A',
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontFamily: 'monospace'
                              }}>
                                {params.formattedValue}
                              </Box>
                            )
                          },
                          {
                            field: 'status',
                            headerName: 'Status',
                            flex: 0.8,
                            minWidth: 100,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Status
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'uppercase'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'details',
                            headerName: 'Details',
                            flex: 2,
                            minWidth: 200,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Details
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Tooltip
                                title={params.value ?
                                  (typeof params.value === 'object' ?
                                    JSON.stringify(params.value, null, 2) :
                                    params.value.toString()) :
                                  'N/A'}
                                arrow
                                placement="top"
                              >
                                <Box sx={{
                                  width: '100%',
                                  '&:hover': {
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: ht.text,
                                    textDecorationThickness: '1px',
                                    textUnderlineOffset: '2px',
                                  }
                                }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      color: ht.text,
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {params.value ?
                                      (typeof params.value === 'object' ?
                                        JSON.stringify(params.value) :
                                        params.value.toString()
                                      ) : 'N/A'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )
                          },
                        ]}
                        getRowId={(row) => row.id || Math.random()}
                        sx={{
                          ...harmonateDataGridSx,
                          border: 'none',
                          '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: ht.listBg,
                            borderBottom: `1px solid ${ht.line}`,
                            minHeight: '40px !important',
                            maxHeight: '40px !important',
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: ht.text,
                          },
                          '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            color: ht.text,
                            borderColor: ht.line,
                          },
                          '& .MuiDataGrid-row:hover': {
                            backgroundColor: ht.secondaryTab,
                          },
                        }}
                        disableRowSelectionOnClick
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: ht.text }}>
                      <Typography>No audit logs found</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <ExcelPreviewDialog open={excelPreviewOpen} onClose={handleCloseExcelPreview} fileId={previewFile?.id} tenantId={getTenantId()} fileName={previewFile?.name} />
      <PDFPreviewDialog open={pdfPreviewOpen} onClose={handleClosePdfPreview} fileId={previewFile?.id} tenantId={getTenantId()} />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteClose}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Delete File</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete{' '}
            <strong>{fileToDelete?.name || 'this file'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleDeleteConfirm} sx={harmonateAbortButtonSx}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankReference;
